import { Check } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { V3Button } from "@/components/ui/v3/button";
import { PreChartLabsChip } from "@/components/ui/visits/pre-chart/PreChartLabsChip";
import { StartVisitButton } from "@/components/ui/visits/StartVisitButton";
import { PreChartBadge } from "@/pages/Visits/redesign/VisitStatusBadge";
import { useScrolledAway } from "./NoteMetaBar";

export interface PreChartActions {
  onStartVisit: () => void;
  onSaveDraft: () => void;
  // Start visit unavailable (e.g. the visit is busy).
  startDisabled?: boolean;
}

// Above a pre-chart draft's note: what it is, how to fill it, and the two
// next steps (Start visit when the patient is seen, or keep it as a draft).
export const PreChartBanner = ({
  onStartVisit,
  onSaveDraft,
  startDisabled = false,
  visitPublicId,
  templateChip,
  onStartScrolledAway,
  coveredBelow = 0,
}: PreChartActions & {
  // The draft: its lab values (PreChartLabsChip).
  visitPublicId?: string;
  // The draft's template, changeable (PreChartTemplateChip).
  templateChip?: React.ReactNode;
  // Start visit here has scrolled out of the note (or is back): the top
  // bar shows its own Start visit only meanwhile, so it is on screen once.
  onStartScrolledAway?: (away: boolean) => void;
  // The Ask dock's height over the note: Start visit under it is away too.
  coveredBelow?: number;
}): JSX.Element => {
  const { t } = useTranslation();
  const actions = useScrolledAway<HTMLDivElement>(
    (away) => onStartScrolledAway?.(away),
    { coveredBelow, half: true },
  );
  return (
    <section
      aria-label={t("preChart.badge")}
      className="rounded-2xl bg-v3-surface-2 p-4 shadow-v3-hairline sm:p-5"
      data-testid="pre-chart-banner"
    >
      <PreChartBadge />
      <p className="mt-2.5 text-[15px] leading-[22px] text-v3-text">
        {t("preChart.banner.title")}
      </p>
      <p className="mt-1 text-[14px] leading-5 text-v3-text-3">
        {t("preChart.banner.body")}
      </p>
      {templateChip && (
        <div className="mt-3 flex min-w-0 flex-wrap items-center gap-2">
          <span className="text-[13px] font-semibold text-v3-text-2">
            {t("preChart.sheet.template")}
          </span>
          {templateChip}
        </div>
      )}
      {visitPublicId && (
        <div className="mt-3 flex min-w-0 flex-wrap items-center gap-2">
          <span className="text-[13px] font-semibold text-v3-text-2">
            {t("preChart.labs.short")}
          </span>
          <PreChartLabsChip visitPublicId={visitPublicId} />
        </div>
      )}
      <div ref={actions} className="mt-4 flex flex-wrap items-center gap-2">
        <StartVisitButton
          disabled={startDisabled}
          testId="pre-chart-start-visit"
          onClick={onStartVisit}
        />
        <V3Button
          data-testid="pre-chart-save-draft"
          variant="secondary"
          onClick={onSaveDraft}
        >
          <Check aria-hidden />
          {t("preChart.saveDraft")}
        </V3Button>
      </div>
    </section>
  );
};
